export const KEYCLOAK_ROLES = {
  ADMIN: 'ADMIN',
  USER: 'USER',
  NetworkAdequacy: 'NetworkAdequacy',
  QA: 'QA',
  Investigator: 'Investigator',
  QA_HMO: 'QA-HMO',
  QA_DHS: 'QA-DHS',
  NetAdExternal: 'NetAdExternal',
  HMOReporting: 'HMOReporting',
  HMOLicensing: 'HMOLicensing',
  PBM: 'PBM',
  EntityAdministrator: 'EntityAdministrator',
};

export const ENTITY_SCOPED_ROLES = new Set([
  KEYCLOAK_ROLES.QA_HMO,
  KEYCLOAK_ROLES.QA_DHS,
  KEYCLOAK_ROLES.NetAdExternal,
  KEYCLOAK_ROLES.HMOReporting,
  KEYCLOAK_ROLES.HMOLicensing,
  KEYCLOAK_ROLES.PBM,
  KEYCLOAK_ROLES.EntityAdministrator,
]);


/**
 * Default email addresses used for notifications.
 */
export const NOTIFICATION_EMAILS = {
  NETWORK_ADEQUACY: 'networkadequacy@state.mn.us',
  QA_EXAMINERS: 'health.qaexaminers@state.mn.us',
};

export const ROLE_TAG_SEVERITY_BY_ROLE = {
  [KEYCLOAK_ROLES.ADMIN]: 'danger',
  [KEYCLOAK_ROLES.QA]: 'info',
  [KEYCLOAK_ROLES.NetworkAdequacy]: 'info',
  [KEYCLOAK_ROLES.Investigator]: 'info',
};

export const ROLE_DISPLAY_LABEL_BY_ROLE = {
  [KEYCLOAK_ROLES.NetAdExternal]: 'Network Adequacy',
  [KEYCLOAK_ROLES.HMOReporting]: 'Annual Reporting',
  [KEYCLOAK_ROLES.HMOLicensing]: 'Licensing Updates',
  [KEYCLOAK_ROLES.PBM]: 'PBM',
  [KEYCLOAK_ROLES.EntityAdministrator]: 'Entity Administrator',
};

export const ROLE_DESCRIPTION_BY_ROLE = {
  [KEYCLOAK_ROLES.NetAdExternal]: 'Will receive notifications related to network adequacy.',
  [KEYCLOAK_ROLES.HMOReporting]: 'Will receive notifications related to annual reporting.',
  [KEYCLOAK_ROLES.HMOLicensing]: 'Will receive notifications related to licensing updates.',
  [KEYCLOAK_ROLES.PBM]: 'Will receive notifications related to PBM network adequacy updates.',
  [KEYCLOAK_ROLES.EntityAdministrator]: 'Can see users and role assignments for their entity and request user removals or role suggestions.',
};

export const getRoleTagSeverity = (role) => {
  return ROLE_TAG_SEVERITY_BY_ROLE[role] ?? 'secondary';
};

export const formatRoleLabel = (role) => {
  return ROLE_DISPLAY_LABEL_BY_ROLE[role] ?? role;
};

export const getNonUserRoleOptions = () => {
  return Object.values(KEYCLOAK_ROLES)
    .filter((role) => role !== KEYCLOAK_ROLES.USER)
    .map((role) => ({
      label: formatRoleLabel(role),
      value: role,
      description: ROLE_DESCRIPTION_BY_ROLE[role] ?? '',
    }));
};

export const ENTITY_TYPES = {
  CBP: 'CBP',
  HMO: 'HMO',
  InsuranceCompany: 'Insurance Company',
  PBM: 'PBM',
};

export const ENTITY_STATUSES = {
  Active: 'Active',
  Inactive: 'Inactive',
};

export const NETWORK_STATUSES = {
  Reviewing: 'Reviewing',
  Active: 'Active',
  Inactive: 'Inactive',
};

export const ENTITY_ADMIN_REQUEST_TYPES = {
  Removal: 'REMOVAL',
  RoleSuggestion: 'ROLE_SUGGESTION',
};

export const ENTITY_ADMIN_REQUEST_STATUSES = {
  Reviewing: 'Reviewing',
  Approved: 'Approved',
  Rejected: 'Rejected',
};

export const NETWORK_MARKET_TYPE = {
  Individual: 'Individual',
  SmallGroup: 'Small Group',
  Dental: 'Dental',
  PBM: 'PBM',
  TBD: 'TBD',
};

export const ENTITY_LIMIT_HMO = 5;
export const ENTITY_LIMIT_PBM = 10;

export const ATTRIBUTE_TYPES = {
  Document: 'Document Type',
  HceTransaction: 'HCET Transaction Type',
  HceEntity: 'HCET Entity Type',
  HceContactAccess: 'HCET Contact Access',
  HceContactRole: 'HCET Contact Role',
  HceTransactionStatus: 'HCET Transaction Status',
  BusinessUnit: 'Business Unit Type',
  Affiliate: 'Affiliate Type',
  ContactRole: 'Contact Role',
  HpSection: 'HP Section',
};

export const ICONS = {
  InfoCircle: 'pi pi-info-circle',
  EditUser: 'pi pi-user-edit',
  ExclamationTriangle: 'pi pi-exclamation-triangle',
  CheckCircle: 'pi pi-check-circle',
  Edit: 'pi pi-pencil',
  Check: 'pi pi-check',
  Close: 'pi pi-times',
  ErrorCircle: 'pi pi-times-circle',
  View: 'pi pi-external-link',
  User: 'pi pi-user',
  Add: 'pi pi-plus-circle',
  Download: 'pi pi-download',
  Upload: 'pi pi-upload',
  Previous: 'pi pi-chevron-left',
  Next: 'pi pi-chevron-right',
  Trash: 'pi pi-trash',
  Home: 'pi pi-home',
  Attach: 'pi pi-paperclip',
  QuestionCircle: 'pi pi-question-circle',
  Users: 'pi pi-users',
};

export const INPUT_MASKS = {
  Phone: '(999) 999-9999',
  PostalCode: '99999?-9999',
};

export const TOAST_SAVE_LIFE = 5000;

export const EXAM_TYPES = {
  MidCycle: 'Mid-cycle (Stand-alone)',
  FullCycle: 'Full Cycle',
};

export const EXAM_PRODUCTS = {
  QaeMhcp: 'QAE MHCP',
  QaeCommercial: 'QAE Commercial',
  Tca: 'TCA',
};

export const EXAM_PHASES = {
  Phase1: 'Phase 1- Planning',
  Phase2: 'Phase 2- Virtual Exam',
  Phase3: 'Phase 3- Discussion and Completion of Exam',
};

export const QA_EXAM_CLARIFICATION_MODULE_TYPES = {
  QualityAssessmentImprovement: 'Quality Assessment and Improvement',
  ComplaintsAppealsCommercialProducts: 'Complaints and Non-clinical Appeals in Commercial Products',
  MHCP_MCProducts: 'Grievance system for MHCP-MC Products',
  CoC: 'Access, Availability & Continuity of Care (CoC)',
  UM: 'UM',
  QualityCareComplaints: 'Quality of Care Complaints',
  FileUniverse_Delegated: '4th File Universe_Delegated Entity Table',
}

export const TCA_CLARIFICATION_MODULE_TYPES = {
  EL2: 'EL 2 Information Systems',
  EL3: 'EL 3 UM',
  EL4: 'EL 4 Special Health Care Needs',
  EL5: 'EL 5 Practice Guidelines',
  EL8: 'EL 8 PIP Interim Reports',
  EL9: 'EL 9 PHM',
  EL10: 'EL 10 Advanced Directives',
  EL11: 'EL 11 Elderly Waiver Care Plans',
  EL12: 'EL 12 Subcontractors',
}

export const IDR_MODULE_TYPES = {
  Background: 'Background',
  QualityAssessmentImprovement: 'Quality Assessment and Improvement',
  ComplaintsAppealsCommercialProducts: 'Complaints and Non-clinical Appeals in Commercial Products',
  GrievanceSystemMHCPMC: 'Grievance System for MHCP-MC Products',
  AccessAvailabilityContinuityOfCare: 'Access, Availability, and Continuity of Care',
  UtilizationManagement: 'Utilization Management (UM)',
}

export const SAMPLE_FILE_TYPES = {
  FileUniverse: 'File Universe',
  SampleFiles: 'Sample Files',
}

export const ALERT_TYPES = {
  Success: 'success',
  Error: 'error',
  Info: 'info',
  Warning: 'warning',
};

export const EXTERNAL_APPEALS_STATUSES = {
  Open: 'Open',
  InfoNeeded: 'Info Needed',
  Closed: 'Closed',
  ClosedTimelyFiling: 'Closed - Timely Filing',
  ClosedCommerceJurisdiction: 'Closed - Commerce Jurisdiction',
  ClosedDHSJurisdiction: 'Closed - DHS Jurisdiction',
  ClosedCMSJurisdiction: 'Closed - CMS Jurisdiction',
  ClosedUSDOLJurisdiction: 'Closed - U.S. DOL Jurisdiction',
  ClosedFiledAsComplaint: 'Closed - Filed As Complaint',
  ClosedWithdrawn: 'Closed - Withdrawn',
};

export const EXTERNAL_APPEALS_OUTCOMES = {
  Overturned: 'Overturned',
  PartiallyOverturned: 'Partially Overturned',
  Upheld: 'Upheld',
  Pending: 'Pending',
  WithdrawnEnrollee: 'Withdrawn - Enrollee',
  WithdrawnInsuranceReversedDecision: 'Withdrawn - Insurance Reversed Decision',
  NotApplicable: 'Not Applicable',
};

export const EXTERNAL_APPEALS_REVIEW_ORGANIZATIONS = {
  RoffeHHC: 'Roffe HHC',
  MMROCorevisory: 'MMRO Corevisory',
  MichiganPeerReview: 'Michigan Peer Review (iMPROve)',
};

export const EXTERNAL_APPEALS_CATEGORIES = {
  ClaimCoverage: 'Claim Coverage',
  PriorAuthorization: 'Prior Authorization',
  PharmacyCoverage: 'Pharmacy Coverage',
  WeightLoss: 'Weight Loss',
  OutOfNetwork: 'Out-of-Network Provider',
  Intracept: 'Intracept',
  PreventativeServices: 'Preventative Services',
};

export const EXTERNAL_APPEALS_PATIENT_RELATIONSHIPS = {
  Self: 'Self',
  Spouse: 'Spouse',
  Parent: 'Parent',
  Guardian: 'Guardian',
  HealthCareProvider: 'Health Care Provider',
  Other: 'Other',
};

export const EXTERNAL_APPEALS_HEALTH_INSURANCE_COMPANIES = {
  AllinaHealthAetna: 'Allina Health & Aetna Health Plan',
  BlueCrossBlueShield: 'Blue Cross & Blue Shield/Blue Plus',
  HealthPartners: 'HealthPartners',
  HennepinHealth: 'Hennepin Health',
  HumanaWI: 'Humana of WI',
  ItascaMedicalCare: 'Itasca Medical Care',
  Medica: 'Medica Health Plan',
  PrimeWest: 'PrimeWest',
  Quartz: 'Quartz',
  SanfordMN: 'Sanford Health Plan of MN',
  SouthCountry: 'South Country Health Alliance',
  UCare: 'UCare',
  UnitedHealthcareIL: 'United HealthCare of IL',
  Other: 'Other',
};

export const EXTERNAL_APPEALS_TYPE_OF_COVERAGES = {
  Group: 'Group (Employer) Coverage',
  Individual: 'Individual (MNSure)',
  PMAP: 'PMAP/Medical Assistance',
  MinnesotaCare: 'MinnesotaCare',
  Medicare: 'Medicare',
  Other: 'Other',
};

export const EXTERNAL_APPEALS_INTERNAL_APPEAL_CONFIRMATION_OPTIONS = {
  Filed: 'I have filed an appeal with my HMO',
  NotFiled: 'I have NOT filed an appeal with my HMO',
  DontKnow: 'I don\'t know',
};

export const CONSUMER_COMPLAINT_PATIENT_RELATIONSHIPS = {
  Self: 'Self',
  Spouse: 'Spouse',
  Parent: 'Parent',
  Guardian: 'Guardian',
  HealthCareProvider: 'Health Care Provider',
  Other: 'Other',
};

export const CONSUMER_COMPLAINT_HEALTH_INSURANCE_COMPANIES = {
  AllinaHealthAetna: 'Allina Health & Aetna Health Plan',
  BlueCrossBlueShield: 'Blue Cross & Blue Shield/Blue Plus',
  HealthPartners: 'HealthPartners',
  HennepinHealth: 'Hennepin Health',
  HumanaWI: 'Humana of WI',
  ItascaMedicalCare: 'Itasca Medical Care',
  Medica: 'Medica Health Plan',
  PrimeWest: 'PrimeWest',
  Quartz: 'Quartz',
  SanfordMN: 'Sanford Health Plan of MN',
  SouthCountry: 'South Country Health Alliance',
  UCare: 'UCare',
  UnitedHealthcareIL: 'United HealthCare of IL',
  Other: 'Other',
};

export const CONSUMER_COMPLAINT_TYPE_OF_COVERAGES = {
  Group: 'Group (Employer) Coverage',
  Individual: 'Individual (MNSure)',
  PMAP: 'PMAP/Medical Assistance',
  MinnesotaCare: 'MinnesotaCare',
  Medicare: 'Medicare',
  Other: 'Other',
};

export const CONSUMER_COMPLAINT_STATUSES = {
  Default: 'Pending Information Insurance',
  Open: 'Open',
  PendingInformationInsurance: 'Pending Information Insurance',
  PendingInformationProvider: 'Pending Information Provider',
  PendingInformationIndividual: 'Pending Information Individual',
  Closed: 'Closed',
  Withdrawn: 'Withdrawn',
};

export const CONSUMER_COMPLAINT_OUTCOMES = {
  ResolvedNoFault: 'Resolved - No Fault',
  ResolvedMutualAgreement: 'Resolved - Mutual Agreement',
  ResolvedWithdrawn: 'Resolved - Withdrawn',
  ReferredToDHS: 'Referred to DHS',
  ReferredToCommerce: 'Referred to Commerce',
  ReferredToMMB: 'Referred to MMB',
  ReferredToCMS: 'Referred to CMS',
  ReferredToLabor: 'Referred to US Dept. of Labor',
  EnforcementNeed: 'Enforcement needed- forward to supervisor',
  ReferredOther: 'Referred Other',
  DateOfInformationInsurance: 'Date of Information due from Insurance (Manual entry)',
  DateOfInformationProvider: 'Date of Information due from Provider (Manual entry)',
};

export const CONSUMER_COMPLAINT_CATEGORIES = {
  ClaimCoverage: 'Claim Coverage',
  BalanceBilling: 'Balance Billing',
  NoSurprisesAct: 'No Surprises Act',
  MentalHealthParity: 'Mental Health Parity',
  NetworkAdequacy: 'Network Adequacy',
  QualityOfCare: 'Quality of Care',
  Enrollment: 'Enrollment',
  ProviderQuality: 'Provider Quality',
  Undetermined: 'Undetermined',
  PharmacyBenefits: 'Pharmacy Benefits',
  CsInsurance: 'CS - Insurance',
  CsProvider: 'CS - Provider',
  OutOfNetwork: 'Out of network provider',
};

export const CONSUMER_COMPLAINT_MDH_INVESTIGATORS = {
  EcklundAndrea: 'Ecklund, Andrea',
  PetersonKirsten: 'Peterson, Kirsten'
};

export const HMO_CDP_UPDATE_DOCUMENTS = {
   Incorporation:"Articles of Incorporation",
   Bylaws:"Bylaws",
   Conflict:"Conflict of Interest Policy",
   Provider:"Provider contracts",
   Complaint:"Internal complaint and appeal procedures",
   Authorization:"Prior Authorization Procedures",
   Agreements:"Agreements with other insurer",
   Enrollee:"Enrollee participation mechanism",
   Financial:"Marketing and Financial plan",
   Administrative:"Administrative Services Agreements/Management Agreements",
   Cybersecurity:"Cybersecurity Event Reporting",
   HMOPlans:"HMO Collaboration Plans",
   Other: "Other"
};

export const ACCEPTED_FILE_TYPES = {
  Pdf:'application/pdf',
  Xlsx:'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  Xlsm:'application/vnd.ms-excel.sheet.macroenabled.12',
  Docx:'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  Xls:'application/vnd.ms-excel',
  Doc:'application/msword',
  Jpeg:'image/jpeg',
  tiff: 'image/tiff',
  Png:'image/png',
  Zip:'application/zip',
  ZipCompressed:'application/x-zip-compressed',
  Wav:'audio/wav',
  Csv:'text/csv',
  Text:'text/plain'
};

export const EXTERNAL_APPEALS_FORWARD_AUTHORIZATION_OPTIONS = {
  Authorized: 'I authorize MDH to forward my external appeal to the appropriate state agency.',
  NotAuthorized: 'I DO NOT authorize MDH to forward my external appeal to the appropriate state agency.',
};

export const CONSUMER_COMPLAINT_TYPES = {
  InsuranceClaimError: "My insurance company incorrectly processed my claim",
  PrescriptionDenied: "My prescription drug was denied",
  PriorAuthDenied: "My prior authorization for a procedure or service was denied",
  ProviderOutOfNetwork: "My provider was out-of-network",
  CoverageTerminated: "My coverage was terminated",
  PreventiveServiceBilling: "I am being billed for a preventive service",
  NoSurprisesAct: "My complaint is related to the No Surprises Act",
  BalanceBilling: "I am being balance billed by my provider",
  QualityOfCare: "My complaint is about the quality of care I've received from my provider",
  SomethingElse: "Something else",
};

export const NOTE_TYPES = {
    Document: 'document',
    Appeal: 'appeal',
    Complaint: 'complaint',
};

export const NOTE_MAX_LENGTH = 240;

// Helper function for Form Select/Options lists.
export const getAsFormOptions = (c) => {
  return Object.entries(c).map(([key, value]) => ({
    label: value,
    value: key,
  }));
};
